import { LoadingSpinner } from "components/LoadingSpinner";
import {
  orderTypeConstants,
  ED_ORDER_TYPE_EXPIRATION_PERIOD,
} from "constants/order";
import { useUserWithAccount } from "context/UserAccountProvider";
import { centsToDollars } from "helpers/price";
import useDocumentTitle from "hooks/useDocumentTitle";
import usePrice from "hooks/usePrice";
import { useQuery } from "hooks/useQuery";
import { isEmpty } from "lodash";
import { DEFAULT_EXPIRATION_DAYS } from "pages/OrderOverview/utils/constants";
import { Button } from "primereact/button";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useGetPublicOrderQuery } from "store/queries/order";
import { onChangeAccountInfo } from "store/slices/order";
import { IOrder } from "types/Order/Order";
import { IPaymentPlan } from "types/Payment";
import { IProvider } from "types/Provider/Provider";

import {
  StyledBreadcrumbContainer,
  StyledBreadCrumb,
  StyledPageContainer,
} from "../../Overview/styled";
import { PaymentSuccessFull } from "../../PaymentSection/PaymentSuccessFull/PaymentSuccessFull";
import { PayNowSection } from "../../PayNowSection";

export function PayNow() {
  useDocumentTitle("Pay More Section");
  const { id: externalId } = useParams();
  const query = useQuery();
  const { userInfo } = useUserWithAccount();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { getClearPriceByOrderType } = usePrice();
  const internalUser = query.get("internalUser");

  const [showPaymentSection, setShowPaymentSection] = useState({
    paymentSection: false,
    paymentPlan: false,
    paymentSuccess: false,
    receiptAvailable: true,
  });

  const {
    data: orderData,
    isLoading: getPublicOrderIsLoading,
    isError: getPublicOrderError,
    isFetching: getPublicOrderIsFetching,
    refetch: refetchPublicOrder,
  } = useGetPublicOrderQuery(externalId || "");

  const orderInfo = useMemo(() => {
    if (isEmpty(orderData)) return {} as IOrder;

    const accountInfo = orderData.data.account;

    if (accountInfo) {
      dispatch(onChangeAccountInfo({ accountInfo }));
    }

    return orderData.data;
  }, [orderData]);

  const remainingInstallmentsInfo = useMemo(() => {
    if (!orderInfo) return null;
    const activePaymentPlan = orderInfo?.paymentPlan?.find(
      (paymentPlan) => paymentPlan.active && paymentPlan?.installments?.length
    ) as IPaymentPlan;
    if (!activePaymentPlan) return null;
    const { installments } = activePaymentPlan;
    const remainingInstallments = installments?.filter(
      (installment) => !installment.paymentId
    );
    const remainingPayments = remainingInstallments?.length;
    const installmentAmount = remainingInstallments?.[0]?.amountInCents;
    return { remainingPayments, installmentAmount };
  }, [orderInfo]);

  const publicContact = () => {
    const currentProvider = orderInfo.account
      .providers[0] as unknown as IProvider;

    const isVisible =
      currentProvider.publicContactName &&
      currentProvider.publicContactRole &&
      currentProvider.publicContactPhone;

    return {
      isVisible,
      name: currentProvider.publicContactName,
      role: currentProvider.publicContactRole,
      phone: currentProvider.publicContactPhone,
    };
  };

  const prices = getClearPriceByOrderType({ order: orderInfo });
  const activePaymentPlan = orderInfo.paymentPlan?.find(
    (paymentPlan) => !!paymentPlan.active
  );

  const fullPrice = centsToDollars(prices.total);
  const remainingPrice = centsToDollars(
    activePaymentPlan?.remainingAmountInCents || 0
  );
  const totalSaving = centsToDollars(prices.totalSaving);

  const getExpirationDays = () => {
    if (isEmpty(orderData)) return undefined;

    // if (getPublicOrderIsLoading) return DEFAULT_EXPIRATION_DAYS;

    if (
      orderInfo.orderType === orderTypeConstants.ED ||
      orderInfo.orderType === orderTypeConstants.BARIATRICS
    ) {
      return ED_ORDER_TYPE_EXPIRATION_PERIOD;
    }

    // return (orderInfo.account.providers[0] as unknown as IProvider)
    //   .expirationDays;
    return 0;
  };

  const expirationDays = getExpirationDays();
  const breadcrumbItems = useMemo(() => {
    const itemsBase = {
      label: "Estimate",
      template: (
        <Button
          onClick={() => {
            navigate(`/orders/${orderInfo.id}`);
          }}
          className="p-button-text"
        >
          Estimate
        </Button>
      ),
    };
    const checkoutItem = {
      label: "Checkout",
      template: <span className="active">Checkout</span>,
    };
    return [itemsBase, checkoutItem];
  }, [orderInfo]);

  // We can't use the useLoading hook because the clientSecret is required to load the payment for now.
  if (getPublicOrderIsLoading || getPublicOrderIsFetching) {
    return <LoadingSpinner />;
  }

  if (getPublicOrderError) {
    return <Navigate to="/not-found" />;
  }

  if (showPaymentSection.paymentSuccess && externalId && orderInfo) {
    return (
      <PaymentSuccessFull
        publicContact={publicContact}
        orderExternalId={externalId}
        receiptAvailable={showPaymentSection.receiptAvailable}
        showPaymentPlanInfo={
          remainingInstallmentsInfo?.remainingPayments !== 0 &&
          remainingInstallmentsInfo?.installmentAmount !== 0
        }
        newDuration={remainingInstallmentsInfo?.remainingPayments}
        newInstallmentAmount={remainingInstallmentsInfo?.installmentAmount}
        orderUrl={
          internalUser && userInfo ? `/orders/${orderInfo.id}` : undefined
        }
      />
    );
  }

  return (
    <div className="flex flex-column w-full align-items-center">
      {internalUser && (
        <StyledBreadcrumbContainer className="p-3">
          <StyledBreadCrumb model={breadcrumbItems} />
        </StyledBreadcrumbContainer>
      )}
      <StyledPageContainer
        maxWidth={
          showPaymentSection.paymentSection || showPaymentSection.paymentPlan
            ? "500px"
            : undefined
        }
        minWidth={
          showPaymentSection.paymentSection || showPaymentSection.paymentPlan
            ? "500px"
            : undefined
        }
      >
        <PayNowSection
          {...{
            expirationDays,
            orderInfo,
            showPaymentSection,
            setShowPaymentSection,
            clearPrice: remainingPrice,
            totalSaving,
            fullPrice,
            payRemainingPrice: true,
            onUpdateOrder: refetchPublicOrder,
          }}
        />
      </StyledPageContainer>
    </div>
  );
}
